import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "../services/api";

// ─── CustomAlert ──────────────────────────────────────────────────────────────
function CustomAlert({ alert, onClose }) {
    if (!alert) return null;

    const cfg = {
        success: {
            iconBg: "bg-emerald-100",
            iconColor: "text-emerald-500",
            btnGradient: "bg-gradient-to-r from-emerald-400 to-emerald-500",
            btnHover: "hover:from-emerald-500 hover:to-emerald-600",
        },
        error: {
            iconBg: "bg-red-100",
            iconColor: "text-red-500",
            btnGradient: "bg-gradient-to-r from-red-400 to-red-500",
            btnHover: "hover:from-red-500 hover:to-red-600",
        },
        warning: {
            iconBg: "bg-amber-100",
            iconColor: "text-amber-500",
            btnGradient: "bg-gradient-to-r from-amber-400 to-yellow-400",
            btnHover: "hover:from-amber-500 hover:to-yellow-500",
        },
        info: {
            iconBg: "bg-cyan-100",
            iconColor: "text-cyan-500",
            btnGradient: "bg-gradient-to-r from-cyan-400 to-cyan-500",
            btnHover: "hover:from-cyan-500 hover:to-cyan-600",
        },
    };

    const icons = {
        success: (
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5"
            >
                <polyline points="20 6 9 17 4 12" />
            </svg>
        ),
        error: (
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5"
            >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
        ),
        warning: (
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5"
            >
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
        ),
        info: (
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5"
            >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
        ),
    };

    const c = cfg[alert.type] || cfg.info;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
                className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/10 backdrop-blur-sm"
                onClick={onClose}
            >
                <motion.div
                    initial={{ opacity: 0, y: 16, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.98 }}
                    transition={{ duration: 0.22, ease: [0.22, 0.68, 0, 1.2] }}
                    className="bg-white rounded-2xl w-full max-w-xs overflow-hidden shadow-xl ring-1 ring-black/5 p-5"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex items-start gap-3 mb-5">
                        <div
                            className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${c.iconBg} ${c.iconColor}`}
                        >
                            {icons[alert.type]}
                        </div>
                        <div className="pt-0.5">
                            <p className="text-sm font-bold text-gray-800 mb-0.5">
                                {alert.title}
                            </p>
                            <p className="text-xs text-gray-400 leading-relaxed">
                                {alert.message}
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={onClose}
                            className="flex-1 py-2.5 rounded-xl text-xs font-medium text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-all border border-gray-100"
                        >
                            Tutup
                        </button>
                        <button
                            onClick={onClose}
                            className={`flex-[2] py-2.5 rounded-xl text-xs font-semibold text-white transition-all shadow-sm ${c.btnGradient} ${c.btnHover}`}
                        >
                            {alert.btnText || "Mengerti"}
                        </button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

// ─── Loading Overlay ──────────────────────────────────────────────────────────
function LoadingOverlay() {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 z-40 flex items-center justify-center"
            style={{
                backgroundColor: "rgba(0,0,0,0.15)",
                backdropFilter: "blur(8px)",
            }}
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.22, ease: [0.22, 0.68, 0, 1.2] }}
                className="bg-white rounded-2xl px-8 py-7 flex flex-col items-center gap-4"
                style={{
                    boxShadow:
                        "0 20px 60px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)",
                    minWidth: "220px",
                }}
            >
                {/* Spinner */}
                <svg
                    className="-rotate-90"
                    width="40"
                    height="40"
                    viewBox="0 0 40 40"
                >
                    <circle
                        cx="20"
                        cy="20"
                        r="16"
                        fill="none"
                        stroke="#e2edef"
                        strokeWidth="3"
                    />
                    <circle
                        cx="20"
                        cy="20"
                        r="16"
                        fill="none"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeDasharray="50 101"
                        stroke="url(#lg)"
                        style={{ animation: "spin-dash 0.9s linear infinite" }}
                    />
                    <defs>
                        <linearGradient
                            id="lg"
                            x1="0%"
                            y1="0%"
                            x2="100%"
                            y2="0%"
                        >
                            <stop offset="0%" stopColor="#22d3ee" />
                            <stop offset="100%" stopColor="#0891b2" />
                        </linearGradient>
                    </defs>
                </svg>

                <div className="flex flex-col items-center gap-0.5 text-center">
                    <p
                        className="text-[0.82rem] font-semibold text-[#0d1a1c]"
                        style={{
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                        }}
                    >
                        Memverifikasi...
                    </p>
                    <p className="text-[0.72rem] text-[#8fa3a8]">
                        Harap tunggu sebentar
                    </p>
                </div>
            </motion.div>
        </motion.div>
    );
}

// ─── Login ────────────────────────────────────────────────────────────────────
function Login() {
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        login: "",
        password: "",
    });

    const [isLoading, setIsLoading] = useState(false);
    const [alertData, setAlertData] = useState(null);
    const [showPassword, setShowPassword] = useState(false);

    const showAlert = (type, title, message, btnText) =>
        setAlertData({ type, title, message, btnText });
    const closeAlert = () => setAlertData(null);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const response = await api.post("/login", formData);

            const user = response.data.user;
            const userRole = (user.role || "").toLowerCase().trim();

            // ── Hybrid Routing Guard ──────────────────────────────────────
            // Central /login is ONLY for Clinic Admins and Superadmins.
            // Patients must use their clinic-specific /:slug/login link.
            if (userRole === "user") {
                setIsLoading(false);
                showAlert(
                    "warning",
                    "Gunakan Link Klinik Anda",
                    "Halaman ini hanya untuk Admin dan Superadmin. Pasien harus login melalui link khusus yang diberikan oleh klinik Anda (contoh: qease.com/nama-klinik/login).",
                    "Mengerti",
                );
                return;
            }

            // Persist session
            localStorage.setItem("token", response.data.token);
            localStorage.setItem("user", JSON.stringify(user));

            if (userRole === "admin") {
                const slug = user.klinik?.slug;
                if (slug) {
                    navigate(`/${slug}/clinic-admin`);
                } else {
                    navigate("/clinic-admin");
                }
            } else if (userRole === "super") {
                navigate("/super-admin");
            } else if (userRole === "doctor") {
                const dSlug = user.dokter?.klinik?.slug || user.klinik?.slug;
                navigate(
                    dSlug ? `/${dSlug}/dashboard/doctor` : "/doctor-dashboard",
                );
            } else {
                navigate("/");
            }
        } catch (error) {
            if (error.response && error.response.data) {
                showAlert(
                    "error",
                    "Login Gagal",
                    error.response.data.message || "Email atau password salah.",
                    "Coba Lagi",
                );
            } else {
                showAlert(
                    "error",
                    "Koneksi Gagal",
                    "Gagal terhubung ke server.",
                    "Coba Lagi",
                );
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div
            className="font-['DM_Sans',sans-serif] w-screen h-screen flex overflow-hidden"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
            <CustomAlert alert={alertData} onClose={closeAlert} />
            <AnimatePresence>{isLoading && <LoadingOverlay />}</AnimatePresence>

            {/* ── LEFT PANEL ── */}
            <div
                className="hidden lg:flex w-1/2 shrink-0 relative overflow-hidden items-center justify-center"
                style={{
                    background:
                        "linear-gradient(150deg, #0e7490 0%, #0891b2 40%, #22d3ee 80%, #06b6d4 100%)",
                }}
            >
                <div
                    className="absolute rounded-full pointer-events-none"
                    style={{
                        top: "-90px",
                        right: "-70px",
                        width: "350px",
                        height: "350px",
                        background: "rgba(255,255,255,0.07)",
                        animation: "orb-float 7s ease-in-out infinite",
                    }}
                />
                <div
                    className="absolute rounded-full pointer-events-none"
                    style={{
                        bottom: "-70px",
                        left: "-50px",
                        width: "260px",
                        height: "260px",
                        background: "rgba(255,255,255,0.05)",
                        animation: "orb-float 9s ease-in-out infinite reverse",
                    }}
                />
                <div
                    className="absolute rounded-full"
                    style={{
                        top: "50px",
                        left: "30px",
                        width: "130px",
                        height: "130px",
                        border: "1px solid rgba(255,255,255,0.13)",
                        background: "rgba(255,255,255,0.06)",
                        animation: "orb-float 6s ease-in-out infinite",
                    }}
                />
                <div
                    className="absolute rounded-full"
                    style={{
                        top: "50%",
                        right: "-25px",
                        transform: "translateY(-50%)",
                        width: "200px",
                        height: "200px",
                        border: "1px solid rgba(255,255,255,0.09)",
                        background: "rgba(255,255,255,0.04)",
                        animation: "orb-float 8s ease-in-out infinite reverse",
                    }}
                />

                <div
                    className="relative z-10 p-10 flex flex-col items-center text-center"
                    style={{
                        animation:
                            "slide-left 0.7s cubic-bezier(.22,.68,0,1.2) both",
                    }}
                >
                    <div
                        className="inline-flex items-center gap-[7px] rounded-full px-4 py-1.5 mb-5 backdrop-blur-sm"
                        style={{
                            background: "rgba(255,255,255,0.15)",
                            border: "1px solid rgba(255,255,255,0.25)",
                        }}
                    >
                        <div
                            className="w-1.5 h-1.5 rounded-full bg-white"
                            style={{
                                animation: "dot-pulse 2s ease-in-out infinite",
                            }}
                        />
                        <span className="text-[0.68rem] font-semibold tracking-[0.1em] uppercase text-white/90">
                            Sistem Manajemen Klinik
                        </span>
                    </div>

                    <h1
                        className="text-[2rem] font-bold text-white leading-tight mb-3 tracking-tight"
                        style={{
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                        }}
                    >
                        Kesehatan Lebih
                        <br />
                        <span>Mudah,</span> Pelayanan
                        <br />
                        Lebih Cepat.
                    </h1>
                    <p className="text-[0.85rem] text-white/70 leading-relaxed max-w-[280px] font-light mb-6">
                        Pantau antrian, kelola jadwal dokter, dan akses data
                        pasien secara real-time dengan platform CareLine.
                    </p>

                    <div className="flex flex-col gap-2.5 w-full max-w-[290px]">
                        {[
                            {
                                delay: "0.2s",
                                icon: (
                                    <svg
                                        width="15"
                                        height="15"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="white"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <rect
                                            x="3"
                                            y="4"
                                            width="18"
                                            height="18"
                                            rx="2"
                                        />
                                        <line x1="16" y1="2" x2="16" y2="6" />
                                        <line x1="8" y1="2" x2="8" y2="6" />
                                        <line x1="3" y1="10" x2="21" y2="10" />
                                    </svg>
                                ),
                                title: "Antrian Real-Time",
                                desc: "Pantau dan kelola antrian secara langsung",
                            },
                            {
                                delay: "0.33s",
                                icon: (
                                    <svg
                                        width="15"
                                        height="15"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="white"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                        <circle cx="9" cy="7" r="4" />
                                        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                    </svg>
                                ),
                                title: "Multi-Role Akses",
                                desc: "Admin, Dokter, dan Pasien dalam satu sistem",
                            },
                            {
                                delay: "0.46s",
                                icon: (
                                    <svg
                                        width="15"
                                        height="15"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="white"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                    </svg>
                                ),
                                title: "Data Aman & Terenkripsi",
                                desc: "Keamanan data pasien terjamin",
                            },
                        ].map((feat, i) => (
                            <div
                                key={i}
                                className="flex items-center gap-2.5 rounded-xl p-2.5 text-left backdrop-blur-sm"
                                style={{
                                    background: "rgba(255,255,255,0.1)",
                                    border: "1px solid rgba(255,255,255,0.15)",
                                    animation: `fade-up 0.5s ease ${feat.delay} both`,
                                }}
                            >
                                <div
                                    className="w-[30px] h-[30px] rounded-lg flex items-center justify-center shrink-0"
                                    style={{
                                        background: "rgba(255,255,255,0.2)",
                                    }}
                                >
                                    {feat.icon}
                                </div>
                                <div>
                                    <strong className="block text-[0.78rem] font-semibold text-white mb-px">
                                        {feat.title}
                                    </strong>
                                    <span className="text-[0.7rem] text-white/60 font-light">
                                        {feat.desc}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── RIGHT PANEL ── */}
            <div className="flex-1 h-screen overflow-hidden bg-[#f4f8f9] flex items-center justify-center p-5 relative">
                <div
                    className="absolute top-0 right-0 w-40 h-40 pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(circle at top right, rgba(8,145,178,0.07) 0%, transparent 70%)",
                    }}
                />

                <div
                    className="bg-white rounded-2xl px-8 py-7 w-full max-w-[390px]"
                    style={{
                        boxShadow:
                            "0 1px 2px rgba(0,0,0,0.04), 0 6px 22px rgba(8,145,178,0.1), 0 0 0 1px rgba(0,0,0,0.04)",
                        animation:
                            "slide-right 0.65s cubic-bezier(.22,.68,0,1.2) both",
                    }}
                >
                    {/* Logo Area */}
                    <div className="flex flex-col items-center mb-4">
                        <img
                            src="/careline-logo.svg"
                            alt="CareLine Logo"
                            className="h-[34px] w-auto mb-[7px]"
                            style={{
                                animation:
                                    "pop-in 0.5s cubic-bezier(.34,1.56,.64,1) 0.1s both",
                            }}
                        />
                        <h2
                            className="text-[1.28rem] font-bold text-[#0d1a1c] mb-0.5 text-center"
                            style={{
                                fontFamily: "'Plus Jakarta Sans', sans-serif",
                            }}
                        >
                            Selamat Datang
                        </h2>
                        <p className="text-[0.78rem] text-[#8fa3a8] text-center">
                            Masuk ke akun CareLine Anda
                        </p>
                    </div>

                    {/* Divider */}
                    <div
                        className="h-px my-3.5"
                        style={{
                            background:
                                "linear-gradient(to right, transparent, rgba(8,145,178,0.18), transparent)",
                        }}
                    />

                    {/* Form */}
                    <form onSubmit={handleSubmit}>
                        <div className="mb-3">
                            <label className="block text-[0.7rem] font-semibold tracking-[0.07em] uppercase text-[#4a6568] mb-1.5">
                                Email / Username
                            </label>
                            <input
                                type="text"
                                name="login"
                                value={formData.login}
                                onChange={handleChange}
                                placeholder="Masukkan Email atau Username anda"
                                className="w-full px-3.5 py-2.5 rounded-[10px] text-[0.85rem] text-[#0d1a1c] bg-[#f8fbfc] outline-none transition-all duration-200 placeholder:text-[#b0c4c8] placeholder:font-light focus:bg-white"
                                style={{
                                    border: "1.5px solid #e2edef",
                                    fontFamily: "'DM Sans', sans-serif",
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = "#0891b2";
                                    e.target.style.boxShadow =
                                        "0 0 0 3px rgba(8,145,178,0.11)";
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = "#e2edef";
                                    e.target.style.boxShadow = "none";
                                }}
                                required
                            />
                        </div>

                        <div className="mb-3">
                            <label className="block text-[0.7rem] font-semibold tracking-[0.07em] uppercase text-[#4a6568] mb-1.5">
                                Kata Sandi
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    placeholder="Masukkan kata sandi anda"
                                    className="w-full px-3.5 py-2.5 pr-10 rounded-[10px] text-[0.85rem] text-[#0d1a1c] bg-[#f8fbfc] outline-none transition-all duration-200 placeholder:text-[#b0c4c8] placeholder:font-light focus:bg-white"
                                    style={{
                                        border: "1.5px solid #e2edef",
                                        fontFamily: "'DM Sans', sans-serif",
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = "#0891b2";
                                        e.target.style.boxShadow =
                                            "0 0 0 3px rgba(8,145,178,0.11)";
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = "#e2edef";
                                        e.target.style.boxShadow = "none";
                                    }}
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() =>
                                        setShowPassword(!showPassword)
                                    }
                                    className="absolute right-[11px] top-1/2 -translate-y-1/2 bg-transparent border-none cursor-pointer text-[#8fa3a8] p-1 flex items-center transition-colors duration-200 hover:text-[#0891b2]"
                                >
                                    {showPassword ? (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                    ) : (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full py-[11px] mt-4 rounded-[10px] border-none text-white text-[0.86rem] font-semibold tracking-[0.04em] cursor-pointer transition-all duration-200 disabled:opacity-[0.58] disabled:cursor-not-allowed hover:enabled:-translate-y-px active:enabled:translate-y-0"
                            style={{
                                background:
                                    "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)",
                                boxShadow: "0 4px 14px rgba(8,145,178,0.32)",
                                fontFamily: "'DM Sans', sans-serif",
                            }}
                            onMouseEnter={(e) => {
                                if (!isLoading)
                                    e.target.style.boxShadow =
                                        "0 6px 18px rgba(8,145,178,0.42)";
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.boxShadow =
                                    "0 4px 14px rgba(8,145,178,0.32)";
                            }}
                        >
                            Masuk
                        </button>
                    </form>

                    {/* Register is intentionally disabled here for Admins */}

                    <p className="mt-4 text-center text-[0.68rem] text-[#b0c4c8]">
                        Powered by{" "}
                        <span
                            className="font-bold text-[#0891b2]"
                            style={{
                                fontFamily: "'Plus Jakarta Sans', sans-serif",
                            }}
                        >
                            Qease
                        </span>
                    </p>
                </div>
            </div>

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
                @keyframes orb-float {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-16px); }
                }
                @keyframes slide-left {
                    from { opacity: 0; transform: translateX(-28px); }
                    to   { opacity: 1; transform: translateX(0); }
                }
                @keyframes slide-right {
                    from { opacity: 0; transform: translateX(26px); }
                    to   { opacity: 1; transform: translateX(0); }
                }
                @keyframes dot-pulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50% { opacity: 0.4; transform: scale(0.72); }
                }
                @keyframes fade-up {
                    from { opacity: 0; transform: translateY(10px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes pop-in {
                    from { opacity: 0; transform: scale(0.82); }
                    to   { opacity: 1; transform: scale(1); }
                }
                @keyframes spin-dash {
                    0%   { stroke-dashoffset: 0; }
                    100% { stroke-dashoffset: -101; }
                }
            `}</style>
        </div>
    );
}

export default Login;